import * as api from "utils/api";
import { message, errorMessage } from "actions/message";
import { browserHistory } from "browser-history";

export const Types = {
  // Reads
  REQUEST_PATIENT_OUTREACH_LIST: "REQUEST_PATIENT_OUTREACH_LIST",
  RESPONSE_PATIENT_OUTREACH_LIST: "RESPONSE_PATIENT_OUTREACH_LIST",
  ERROR_PATIENT_OUTREACH_LIST: "ERROR_PATIENT_OUTREACH_LIST",
  REQUEST_PATIENT_OUTREACH_COUNTS: "REQUEST_PATIENT_OUTREACH_COUNTS",
  RESPONSE_PATIENT_OUTREACH_COUNTS: "RESPONSE_PATIENT_OUTREACH_COUNTS",
  ERROR_PATIENT_OUTREACH_COUNTS: "ERROR_PATIENT_OUTREACH_COUNTS",
  REQUEST_PATIENT_OUTREACH_FILTER_OPTIONS:
    "REQUEST_PATIENT_OUTREACH_FILTER_OPTIONS",
  RESPONSE_PATIENT_OUTREACH_FILTER_OPTIONS:
    "RESPONSE_PATIENT_OUTREACH_FILTER_OPTIONS",
  ERROR_PATIENT_OUTREACH_FILTER_OPTIONS:
    "ERROR_PATIENT_OUTREACH_FILTER_OPTIONS",
  REQUEST_ELIGIBILITY_RULES: "REQUEST_ELIGIBILITY_RULES",
  RESPONSE_ELIGIBILITY_RULES: "RESPONSE_ELIGIBILITY_RULES",
  ERROR_ELIGIBILITY_RULES: "ERROR_ELIGIBILITY_RULES",
  REQUEST_ELIGIBILITY_EVALUATION: "REQUEST_ELIGIBILITY_EVALUATION",
  RESPONSE_ELIGIBILITY_EVALUATION: "RESPONSE_ELIGIBILITY_EVALUATION",
  ERROR_ELIGIBILITY_EVALUATION: "ERROR_ELIGIBILITY_EVALUATION",
  // HCPCS eligibility is fetched separately because the BE calc is slow — the FE
  // renders the rest of the eligibility result immediately and shows a spinner in
  // just the HCPCS section while this one loads.
  REQUEST_ELIGIBILITY_HCPCS: "REQUEST_ELIGIBILITY_HCPCS",
  RESPONSE_ELIGIBILITY_HCPCS: "RESPONSE_ELIGIBILITY_HCPCS",
  ERROR_ELIGIBILITY_HCPCS: "ERROR_ELIGIBILITY_HCPCS",
  // Mutations — single
  PATIENT_OUTREACH_UNALLOCATE: "PATIENT_OUTREACH_UNALLOCATE",
  PATIENT_OUTREACH_REMOVE_FROM_SYNTHPOP: "PATIENT_OUTREACH_REMOVE_FROM_SYNTHPOP",
  PATIENT_OUTREACH_POSTPONE: "PATIENT_OUTREACH_POSTPONE",
  // Mutations — bulk
  PATIENT_OUTREACH_UNALLOCATE_BULK: "PATIENT_OUTREACH_UNALLOCATE_BULK",
  PATIENT_OUTREACH_REMOVE_FROM_SYNTHPOP_BULK:
    "PATIENT_OUTREACH_REMOVE_FROM_SYNTHPOP_BULK",
  PATIENT_OUTREACH_POSTPONE_BULK: "PATIENT_OUTREACH_POSTPONE_BULK",
  // Filter/page state
  SET_PATIENT_OUTREACH_FILTERS: "SET_PATIENT_OUTREACH_FILTERS",
  SET_PATIENT_OUTREACH_PAGE: "SET_PATIENT_OUTREACH_PAGE",
  SET_PATIENT_OUTREACH_PAGESIZE: "SET_PATIENT_OUTREACH_PAGESIZE",
  SET_PATIENT_OUTREACH_SORT: "SET_PATIENT_OUTREACH_SORT"
};

// Monotonic counters that protect against out-of-order RESPONSE actions.
// If the user changes filters mid-flight and a stale response lands late,
// its RESPONSE / ERROR dispatch is silently dropped so the slice never
// reverts to data the user has moved past. List and Counts each carry their
// own counter — they share a trigger (filter change) but complete independently.
let listRequestCounter = 0;
let countsRequestCounter = 0;

// Reads
export const fetchPatientOutreachList = payload => async dispatch => {
  const requestId = ++listRequestCounter;
  try {
    dispatch({ type: Types.REQUEST_PATIENT_OUTREACH_LIST });
    const result = await api.put("patients/outreach/admin/list", payload);
    if (requestId !== listRequestCounter) return null;
    dispatch({ type: Types.RESPONSE_PATIENT_OUTREACH_LIST, payload: result });
    return result;
  } catch (e) {
    if (requestId !== listRequestCounter) return null;
    dispatch({ type: Types.ERROR_PATIENT_OUTREACH_LIST });
    dispatch(errorMessage("Failed to load patient outreach list"));
    throw e;
  }
};

// S3DEV2-7602 — Counts now honor the same filter payload as List so the tiles
// update alongside the grid. Null / empty payload yields pool-wide counts (first load).
export const fetchPatientOutreachCounts = (payload = null) => async dispatch => {
  const requestId = ++countsRequestCounter;
  try {
    dispatch({ type: Types.REQUEST_PATIENT_OUTREACH_COUNTS });
    const result = await api.put("patients/outreach/admin/counts", payload || {});
    if (requestId !== countsRequestCounter) return;
    dispatch({ type: Types.RESPONSE_PATIENT_OUTREACH_COUNTS, payload: result });
  } catch (e) {
    if (requestId !== countsRequestCounter) return;
    dispatch({ type: Types.ERROR_PATIENT_OUTREACH_COUNTS });
    dispatch(errorMessage("Failed to load outreach counts"));
  }
};

// Returns only the companies / teams / coaches that have at least one row in
// today's S3_PatientOutreach, so the filter dropdowns aren't padded with
// hundreds of options that wouldn't narrow the grid. Silent on failure —
// the dropdowns just stay empty rather than blocking the page.
export const fetchPatientOutreachFilterOptions = () => async dispatch => {
  try {
    dispatch({ type: Types.REQUEST_PATIENT_OUTREACH_FILTER_OPTIONS });
    const result = await api.get("patients/outreach/admin/filter-options");
    dispatch({
      type: Types.RESPONSE_PATIENT_OUTREACH_FILTER_OPTIONS,
      payload: result
    });
    return result;
  } catch (e) {
    dispatch({ type: Types.ERROR_PATIENT_OUTREACH_FILTER_OPTIONS });
    return null;
  }
};

export const fetchEligibilityRules = () => async dispatch => {
  try {
    dispatch({ type: Types.REQUEST_ELIGIBILITY_RULES });
    const result = await api.get("patients/outreach/admin/eligibility-rules");
    dispatch({
      type: Types.RESPONSE_ELIGIBILITY_RULES,
      payload: result.rules
    });
  } catch (e) {
    dispatch({ type: Types.ERROR_ELIGIBILITY_RULES });
    dispatch(errorMessage("Failed to load eligibility rules"));
  }
};

export const fetchEligibilityEvaluation = patientGuid => async dispatch => {
  try {
    dispatch({
      type: Types.REQUEST_ELIGIBILITY_EVALUATION,
      meta: { patientGuid }
    });
    const result = await api.get(
      `patients/outreach/admin/eligibility/${encodeURIComponent(patientGuid)}`
    );
    dispatch({ type: Types.RESPONSE_ELIGIBILITY_EVALUATION, payload: result });
    return result;
  } catch (e) {
    dispatch({ type: Types.ERROR_ELIGIBILITY_EVALUATION });
    dispatch(errorMessage("Failed to evaluate eligibility"));
    throw e;
  }
};

// Fetched in parallel with fetchEligibilityEvaluation. Separate request so the slow
// HCPCS calc (walks insurance + order history + company settings per equipment type)
// doesn't block rendering of the rest of the eligibility result.
export const fetchEligibilityHcpcs = patientGuid => async dispatch => {
  try {
    dispatch({
      type: Types.REQUEST_ELIGIBILITY_HCPCS,
      meta: { patientGuid }
    });
    const result = await api.get(
      `patients/outreach/admin/eligibility/${encodeURIComponent(patientGuid)}/hcpcs`
    );
    dispatch({ type: Types.RESPONSE_ELIGIBILITY_HCPCS, payload: result });
    return result;
  } catch (e) {
    dispatch({ type: Types.ERROR_ELIGIBILITY_HCPCS });
    // No toast — HCPCS is a bonus section, the rest of the eligibility lookup still
    // works without it. Failure quietly shows an empty state in the HCPCS section.
    return null;
  }
};

// superagent rejects on non-2xx. The BE returns 409 with a body of
// { succeeded:false, blocked:true, reason:"..." } when the active-call
// guardrail trips. Unwrap that here so the action creator behaves the
// same as a 200 from the caller's perspective (`result.blocked === true`
// instead of a thrown error). Re-throw anything else (network / 5xx).
const unwrapBlocked = e => {
  if (e?.status === 409 && e?.response?.body) return e.response.body;
  return null;
};

// Mutations — single. All return the BE response so callers can branch on
// { succeeded, blocked, removedFromTodayPool, ... }.
export const unallocatePatient = patientGuid => async dispatch => {
  try {
    const result = await api.put("patients/outreach/admin/unallocate", {
      patientGuid
    });
    dispatch({
      type: Types.PATIENT_OUTREACH_UNALLOCATE,
      payload: result,
      meta: { patientGuid }
    });
    if (result.succeeded) {
      dispatch(message("Patient unallocated"));
    } else if (result.reason) {
      // The BE can return HTTP 200 with { succeeded: false, reason } for soft
      // failures (e.g. patient not in S3_PatientOutreach today). Surface it so
      // the user knows the action didn't land instead of seeing no feedback.
      dispatch(errorMessage(`Unallocate failed: ${result.reason}`));
    }
    return result;
  } catch (e) {
    const blocked = unwrapBlocked(e);
    if (blocked) {
      dispatch({
        type: Types.PATIENT_OUTREACH_UNALLOCATE,
        payload: blocked,
        meta: { patientGuid }
      });
      return blocked;
    }
    dispatch(errorMessage(`Failed to unallocate: ${e?.message || String(e)}`));
    throw e;
  }
};

export const removePatientFromSynthpop = patientGuid => async dispatch => {
  try {
    const result = await api.put(
      "patients/outreach/admin/remove-from-synthpop",
      { patientGuid }
    );
    dispatch({
      type: Types.PATIENT_OUTREACH_REMOVE_FROM_SYNTHPOP,
      payload: result,
      meta: { patientGuid }
    });
    if (result.succeeded) {
      dispatch(message("Patient removed from Synthpop"));
    } else if (result.reason) {
      // Soft-failure path (e.g. not currently allocated to Synthpop) — surface
      // the BE's reason instead of leaving the user wondering what happened.
      dispatch(errorMessage(`Remove from Synthpop failed: ${result.reason}`));
    }
    return result;
  } catch (e) {
    const blocked = unwrapBlocked(e);
    if (blocked) {
      dispatch({
        type: Types.PATIENT_OUTREACH_REMOVE_FROM_SYNTHPOP,
        payload: blocked,
        meta: { patientGuid }
      });
      return blocked;
    }
    dispatch(
      errorMessage(`Failed to remove from Synthpop: ${e?.message || String(e)}`)
    );
    throw e;
  }
};

export const postponePatientOutreach =
  (patientGuid, newOutreachDate) => async dispatch => {
    try {
      const result = await api.put("patients/outreach/admin/postpone", {
        patientGuid,
        newOutreachDate
      });
      dispatch({
        type: Types.PATIENT_OUTREACH_POSTPONE,
        payload: result,
        meta: { patientGuid }
      });
      if (result.succeeded) {
        dispatch(message(`Outreach postponed to ${newOutreachDate}`));
      } else if (result.reason) {
        dispatch(errorMessage(`Postpone failed: ${result.reason}`));
      }
      return result;
    } catch (e) {
      const blocked = unwrapBlocked(e);
      if (blocked) {
        dispatch({
          type: Types.PATIENT_OUTREACH_POSTPONE,
          payload: blocked,
          meta: { patientGuid }
        });
        dispatch(
          errorMessage(`Postpone blocked: ${blocked.reason || "active call"}`)
        );
        return blocked;
      }
      dispatch(errorMessage(`Failed to postpone: ${e?.message || String(e)}`));
      throw e;
    }
  };

// Mutations — bulk. Same shape; bulk endpoints always return 200 with results[].
export const unallocatePatientsBulk = patientGuids => async dispatch => {
  try {
    const result = await api.put("patients/outreach/admin/unallocate-bulk", {
      patientGuids
    });
    dispatch({
      type: Types.PATIENT_OUTREACH_UNALLOCATE_BULK,
      payload: result,
      meta: { patientGuids }
    });
    dispatch(
      message(
        `Bulk unallocate: ${result.succeeded} succeeded, ${result.blocked} blocked, ${result.noOp + result.errored} other`
      )
    );
    return result;
  } catch (e) {
    dispatch(errorMessage(`Bulk unallocate failed: ${e?.message || String(e)}`));
    throw e;
  }
};

export const removePatientsFromSynthpopBulk = patientGuids => async dispatch => {
  try {
    const result = await api.put(
      "patients/outreach/admin/remove-from-synthpop-bulk",
      { patientGuids }
    );
    dispatch({
      type: Types.PATIENT_OUTREACH_REMOVE_FROM_SYNTHPOP_BULK,
      payload: result,
      meta: { patientGuids }
    });
    dispatch(
      message(`Bulk remove-from-Synthpop: ${result.succeeded} succeeded`)
    );
    return result;
  } catch (e) {
    dispatch(
      errorMessage(
        `Bulk remove-from-Synthpop failed: ${e?.message || String(e)}`
      )
    );
    throw e;
  }
};

export const postponePatientOutreachBulk =
  (patientGuids, newOutreachDate) => async dispatch => {
    try {
      const result = await api.put("patients/outreach/admin/postpone-bulk", {
        patientGuids,
        newOutreachDate
      });
      dispatch({
        type: Types.PATIENT_OUTREACH_POSTPONE_BULK,
        payload: result,
        meta: { patientGuids, newOutreachDate }
      });
      dispatch(message(`Bulk postpone: ${result.succeeded} succeeded`));
      return result;
    } catch (e) {
      dispatch(
        errorMessage(`Bulk postpone failed: ${e?.message || String(e)}`)
      );
      throw e;
    }
  };

// S3DEV2-7602 — Kicks off an async CSV export of the currently-filtered pool.
// The BE queues a background job that lands on /exports; nothing to render on the
// current page. Matches the same fire-and-forget pattern as actions/call-quality
// exportCallQuality.
export const exportPatientOutreach = payload => async dispatch => {
  try {
    await api.post("patients/outreach/admin/export", payload);
    browserHistory.push("/exports");
  } catch (e) {
    dispatch(errorMessage("Failed to start Patient Call Pool export"));
    throw e;
  }
};

// Filter / page setters
export const setFilters = filters => ({
  type: Types.SET_PATIENT_OUTREACH_FILTERS,
  payload: filters
});
export const setPage = page => ({
  type: Types.SET_PATIENT_OUTREACH_PAGE,
  payload: page
});
export const setPageSize = pageSize => ({
  type: Types.SET_PATIENT_OUTREACH_PAGESIZE,
  payload: pageSize
});
export const setSort = sort => ({
  type: Types.SET_PATIENT_OUTREACH_SORT,
  payload: sort
});
